import React from 'react';
import { Button, Card, List, Space, Tag, Typography } from 'antd';
import { useNavigate } from 'react-router-dom';
import type { Quotation } from '@/services/quotation.service';
import { displayQuotationRevisionLabel } from '@/utils/quotationRevisionPolicy';
import { resolveFamilyRole } from '@/utils/quotationRevisionIntelligence';
import { formatMoney, formatPercent, UAE_MONEY_PROFILE } from '@/utils/format';
import dayjs from 'dayjs';

const { Text } = Typography;

type Props = {
  family: Quotation[];
  onCompare?: (id: string) => void;
};

export const QuotationRevisionHistoryTab: React.FC<Props> = ({ family, onCompare }) => {
  const navigate = useNavigate();
  const sorted = [...family].sort((a, b) => Number(b.revision_number) - Number(a.revision_number));

  if (sorted.length === 0) {
    return <Text type="secondary">No revisions yet. This is the original quotation R0.</Text>;
  }

  return (
    <List
      dataSource={sorted}
      renderItem={(r) => {
        const role = resolveFamilyRole(r);
        const rev = displayQuotationRevisionLabel(r.revision_number);
        const marginPct = r.profitability_snapshot?.gross_profit_percentage;

        return (
          <List.Item
            actions={[
              <Button key="open" type="link" size="small" onClick={() => navigate(`/growth/quotations/${r.id}`)}>
                Open
              </Button>,
              onCompare ? (
                <Button key="cmp" type="link" size="small" onClick={() => onCompare(r.id)}>
                  Compare
                </Button>
              ) : null,
            ].filter(Boolean)}
          >
            <List.Item.Meta
              title={
                <Space wrap>
                  <Text strong>{rev}</Text>
                  {role === 'current_offer' ? <Tag color="blue">Current</Tag> : null}
                  {role === 'accepted' ? <Tag color="green">Customer Accepted</Tag> : null}
                  {role === 'superseded' ? <Tag>Superseded</Tag> : null}
                  <Tag>{r.status}</Tag>
                </Space>
              }
              description={
                <Space direction="vertical" size={2}>
                  {r.revision_reason ? <Text type="secondary">{r.revision_reason}</Text> : null}
                  {r.sent_date ? (
                    <Text type="secondary">Submitted {dayjs(r.sent_date).format('DD MMM YYYY')}</Text>
                  ) : null}
                  <Text>
                    Sell {formatMoney(r.grand_total, { ...UAE_MONEY_PROFILE, currency: r.currency || 'AED' })}
                    {marginPct != null
                      ? ` · Margin ${formatPercent(marginPct, { semantics: 'whole', maximumFractionDigits: 2 })}`
                      : ''}
                  </Text>
                </Space>
              }
            />
          </List.Item>
        );
      }}
    />
  );
};

export default QuotationRevisionHistoryTab;
